import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { Metadata, TableRecord } from '../../state/metadata/types';

interface MetadataResponse {
  extra: Metadata;
}

/**
 * Reads shared metadata from the public, unauthenticated endpoint
 * (`public/json/meta-data`). Use this on pages that render before login
 * (e.g. the request-account / request-customer forms) where the NgRx
 * store-backed {@link MetadataService} has no data yet. The result is fetched
 * once and shared between subscribers.
 */
@Injectable({ providedIn: 'root' })
export class PublicMetadataService {
  protected url = 'public/json/meta-data';
  private http = inject(HttpClient);

  private metadata$: Observable<Metadata> = this.http
    .get<MetadataResponse>(this.url)
    .pipe(
      map(resp => resp?.extra ?? {}),
      shareReplay(1)
    );

  /** The full metadata payload (cached). */
  getMetadata(): Observable<Metadata> {
    return this.metadata$;
  }

  /** The `list` array for a metadata collection, or an empty array. */
  getMetadataTableList(tableName: string): Observable<TableRecord[]> {
    return this.metadata$.pipe(map(metadata => metadata[tableName]?.list ?? []));
  }

  /** The `map` (keyed by code) for a metadata collection, or an empty object. */
  getMetadataTableMap(tableName: string): Observable<{ [key: string]: TableRecord }> {
    return this.metadata$.pipe(map(metadata => metadata[tableName]?.map ?? {}));
  }
}
